"use client";

import { type PropsWithChildren, createContext, useContext } from "react";
import type { AgentApiTransport } from "./agent-api";
import type { CaptureChatAnalyticsEvent } from "./chat-analytics";

/**
 * チャット UI が配信面から受け取る設定。パッケージ側は env / 認証基盤 / ルート / 計測基盤を
 * 直接読まず、全てこの config 経由で受け取る(配信面差の注入シーム)。値はコンテキストで配布し、
 * リクエスト関数へは呼び出し側が明示的に渡す(module state を持たない = SSR で安全)。
 *
 * transport に加え、エンゲージメント計測の送信先を captureAnalyticsEvent で受け取る。
 * 計測は任意(未注入でも UI は成立)のため optional。配信面ごとに出し分ける機能フラグ
 * (引用/モデル選択/広告 等)が必要になった時点で capabilities を足す。免責表示は法的に必須のため
 * capability にはしない。
 */
export type ChatConfig = AgentApiTransport & {
  captureAnalyticsEvent?: CaptureChatAnalyticsEvent;
};

const ChatConfigContext = createContext<ChatConfig | null>(null);

export const ChatConfigProvider = ({
  value,
  children,
}: PropsWithChildren<{ value: ChatConfig }>) => (
  <ChatConfigContext.Provider value={value}>
    {children}
  </ChatConfigContext.Provider>
);

export const useChatConfig = (): ChatConfig => {
  const ctx = useContext(ChatConfigContext);
  if (!ctx) {
    throw new Error(
      "useChatConfig は ChatConfigProvider の内側で使用してください",
    );
  }
  return ctx;
};

const noopCapture: CaptureChatAnalyticsEvent = () => {};

/**
 * 計測 capture を緩く読む。useChatConfig と違い Provider 不在・計測未注入でも throw せず
 * no-op を返すため、計測を配線しないテストや配信面でも UI を壊さない(計測は任意)。
 */
export const useChatAnalyticsCapture = (): CaptureChatAnalyticsEvent => {
  const ctx = useContext(ChatConfigContext);
  return ctx?.captureAnalyticsEvent ?? noopCapture;
};
